/**
 * Receipt money checks. Never silently rewrite amounts —
 * the UI offers a suggested split and the user picks it.
 */

export type MoneyFix = {
  id: string;
  label: string;
  net_pence: number;
  vat_pence: number;
  gross_pence: number;
};

/** Net + VAT should equal gross within 1p. Never silently rewrite amounts. */
export function reconcileMoney(opts: {
  net_pence: number | null;
  vat_pence: number | null;
  gross_pence: number | null;
}): { ok: boolean; note: string | null } {
  const { net_pence: net, vat_pence: vat, gross_pence: gross } = opts;
  if (net == null || vat == null || gross == null) {
    return {
      ok: true,
      note: 'Incomplete amounts — reconcile check skipped until all three are present.',
    };
  }
  const sum = net + vat;
  if (Math.abs(sum - gross) <= 1) {
    return { ok: true, note: null };
  }
  return {
    ok: false,
    note: `Net + VAT (£${(sum / 100).toFixed(2)}) does not match gross (£${(gross / 100).toFixed(2)}). Left unchanged — check the receipt.`,
  };
}

function penceLabel(p: number): string {
  return `£${(p / 100).toFixed(2)}`;
}

function sameAmounts(a: MoneyFix, b: MoneyFix): boolean {
  return (
    a.net_pence === b.net_pence &&
    a.vat_pence === b.vat_pence &&
    a.gross_pence === b.gross_pence
  );
}

/**
 * Given any two of net / VAT / gross, offer the third.
 * Also offer a 20% VAT-inclusive split from gross when the three do not add up.
 * Never silently apply — the UI must let the user pick.
 */
export function suggestMoneyFixes(opts: {
  net_pence: number | null;
  vat_pence: number | null;
  gross_pence: number | null;
}): MoneyFix[] {
  const net = opts.net_pence;
  const vat = opts.vat_pence;
  const gross = opts.gross_pence;
  const out: MoneyFix[] = [];
  const add = (fix: MoneyFix) => {
    if (out.some((x) => sameAmounts(x, fix))) return;
    if (fix.vat_pence < 0 || fix.net_pence < 0 || fix.gross_pence < 0) return;
    out.push(fix);
  };

  if (net != null && vat != null) {
    const g = net + vat;
    if (gross == null || Math.abs(g - gross) > 1) {
      add({
        id: 'gross-from-net-vat',
        label: `Set gross to ${penceLabel(g)} (net + VAT)`,
        net_pence: net,
        vat_pence: vat,
        gross_pence: g,
      });
    }
  }
  if (net != null && gross != null) {
    const v = gross - net;
    if (vat == null || Math.abs(v - vat) > 1) {
      add({
        id: 'vat-from-net-gross',
        label: `Set VAT to ${penceLabel(v)} (gross − net)`,
        net_pence: net,
        vat_pence: v,
        gross_pence: gross,
      });
    }
  }
  if (vat != null && gross != null) {
    const n = gross - vat;
    if (net == null || Math.abs(n - net) > 1) {
      add({
        id: 'net-from-vat-gross',
        label: `Set net to ${penceLabel(n)} (gross − VAT)`,
        net_pence: n,
        vat_pence: vat,
        gross_pence: gross,
      });
    }
  }
  if (gross != null && gross > 0) {
    const n = Math.round(gross / 1.2);
    const v = gross - n;
    if (
      net == null ||
      vat == null ||
      Math.abs(n - (net ?? -1)) > 1 ||
      Math.abs(v - (vat ?? -1)) > 1
    ) {
      add({
        id: 'std-from-gross',
        label: `Treat ${penceLabel(gross)} as 20% VAT inclusive (${penceLabel(n)} + ${penceLabel(v)})`,
        net_pence: n,
        vat_pence: v,
        gross_pence: gross,
      });
    }
  }
  return out;
}
